/* eslint-disable react/prop-types */
import { Formik, Form } from "formik";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

// Components & Services
import { BinCardInfo } from "./info";
import { BinCardService } from "services/bin-card";
import { getPrintTemplate } from "./components/printTemplate";

const today = new Date();

const initialValues = {
  unit: "",
  address: "",
  creationDate: "",
  sheetNumber: "01",
  materialName: "",
  materialUnit: "",
  materialCode: "",
  details: [
    {
      ordinalNumber: "01",
      docDate: "",
      inDocNumber: "",
      outDocNumber: "",
      description: "",
      transactionDate: "",
      inQuantity: 0,
      outQuantity: 0,
      balance: 0,
      accountantConfirm: "",
    }
  ],
  totalInQuantity: 0,
  totalOutQuantity: 0,
  closingBalance: 0,
  totalPages: "1",
  openingDate: "",
  reportDate: "",
  ledgerClerk: "",
  chiefAccountant: "",
  legalRepresentative: "",
  signingDay: today.getDate().toString(),
  signingMonth: (today.getMonth() + 1).toString(),
  signingYear: today.getFullYear().toString(),
};

function BinCardDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();
  const [formInitials, setFormInitials] = useState(initialValues);

  useEffect(() => {
    (async () => {
      try {
        if (id) {
          const response = await BinCardService.getById(id);
          const apiData = response.data || response;

          const formatDateVN = (iso) => {
            if (!iso) return "";
            if (typeof iso === "string" && iso.includes("/")) return iso;
            const d = new Date(iso);
            return isNaN(d) ? iso : `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
          };

          let [sDay, sMonth, sYear] = [initialValues.signingDay, initialValues.signingMonth, initialValues.signingYear];
          if (apiData.reportDate?.includes("/")) {
            const pts = apiData.reportDate.split("/");
            if (pts.length === 3) [sDay, sMonth, sYear] = pts;
          }

          setFormInitials({
            ...initialValues,
            unit: apiData.unit || "",
            address: apiData.address || "",
            creationDate: formatDateVN(apiData.creationDate),
            sheetNumber: apiData.sheetNumber || "01",
            materialName: apiData.materialName || "",
            materialUnit: apiData.materialUnit || "",
            materialCode: apiData.materialCode || "",
            details: apiData.details?.length > 0 ? apiData.details : initialValues.details,
            totalInQuantity: apiData.totalInQuantity || 0,
            totalOutQuantity: apiData.totalOutQuantity || 0,
            closingBalance: apiData.closingBalance || 0,
            totalPages: apiData.totalPages || "1",
            openingDate: formatDateVN(apiData.openingDate),
            reportDate: apiData.reportDate || "",
            ledgerClerk: apiData.ledgerClerk || "",
            chiefAccountant: apiData.chiefAccountant || "",
            legalRepresentative: apiData.legalRepresentative || "",
            signingDay: sDay,
            signingMonth: sMonth,
            signingYear: sYear,
          });
        } else {
          setFormInitials(initialValues);
        }
      } catch (e) {
        console.error("Load failed:", e);
        toast.error("Không thể tải dữ liệu");
      }
    })();
  }, [id]);

  const handleBack = () => navigation("/templates/bin-card");

  const submitForm = async (fValues) => {
    try {
      const num = (v) => (v && v !== "" && !isNaN(v) ? Number(v) : 0);

      const payload = {
        unit: fValues.unit,
        address: fValues.address,
        creationDate: fValues.creationDate,
        sheetNumber: fValues.sheetNumber,
        materialName: fValues.materialName,
        materialUnit: fValues.materialUnit,
        materialCode: fValues.materialCode,
        details: (fValues.details || []).map(row => ({
          ...row,
          inQuantity: num(row.inQuantity),
          outQuantity: num(row.outQuantity),
          balance: num(row.balance),
        })),
        totalInQuantity: num(fValues.totalInQuantity),
        totalOutQuantity: num(fValues.totalOutQuantity),
        closingBalance: num(fValues.closingBalance),
        totalPages: fValues.totalPages,
        openingDate: fValues.openingDate,
        ledgerClerk: fValues.ledgerClerk,
        chiefAccountant: fValues.chiefAccountant,
        legalRepresentative: fValues.legalRepresentative,
        reportDate: `${fValues.signingDay.padStart(2, "0")}/${fValues.signingMonth.padStart(2, "0")}/${fValues.signingYear}`,
      };

      if (!id) {
        await BinCardService.create(payload);
        toast.success("Tạo thẻ kho thành công!");
      } else {
        await BinCardService.update(id, payload);
        toast.success("Cập nhật thành công!");
      }
      navigation("/templates/bin-card");
    } catch (e) {
      toast.error(e?.response?.data?.message || "Lưu thất bại!");
    }
  };

  const handlePrint = (pValues) => {
    const html = getPrintTemplate(pValues);
    const win = window.open("", "_blank");
    win.document.open();
    win.document.write(`<html><head><title>Print S12-DN</title>
      <style>@media print{@page{size:A4 portrait;margin:1cm}}body{margin:0}</style>
      </head><body onload="setTimeout(()=>[window.print(),window.close()], 500)">${html}</body></html>`);
    win.document.close();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3} mb={10}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Formik initialValues={formInitials} enableReinitialize onSubmit={submitForm}>
              {({ values, isSubmitting, setFieldValue }) => (
                <Form autoComplete="off">
                  <Card>
                    <SoftBox p={0}>
                      <BinCardInfo formData={{ values, setFieldValue, containView }} />
                      <SoftBox p={3} display="flex" justifyContent="space-between" mt={2}>
                        <SoftButton variant="gradient" color="light" onClick={handleBack}>Trở về</SoftButton>
                        {containView ? (
                          <SoftButton variant="gradient" color="success" onClick={() => handlePrint(values)}>In Thẻ Kho S12-DN</SoftButton>
                        ) : (
                          <SoftButton disabled={isSubmitting} type="submit" variant="gradient" color="dark">{id ? "Lưu" : "Tạo mới"}</SoftButton>
                        )}
                      </SoftBox>
                    </SoftBox>
                  </Card>
                </Form>
              )}
            </Formik>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

export default BinCardDetail;
